import * as z from 'zod'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Brain, Info } from 'lucide-react'
import nsdaResolutions from '@/static/nsda_resolutions_2025_2026.json'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAxios } from '@/contexts/AxiosProvider'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

import { formSchema } from './page'
import { DebateFormatFormField } from '@/components/settings/DebateFormatFormField'

export const BasicDetails = ({ form, onNext, controlBtns, setIsAIProcessing, isAIProcessing }) => {
  const axios = useAxios()

  const isLoading = form.formState.isSubmitting

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    if (onNext) onNext(values)
  }

  const onErrors = (errors: any) => {}

  const handleGenAiDebateProposition = async () => {
    setIsAIProcessing(true)
    try {
      const res: any = await axios.get('/ai/resolution', {
        params: { topic: form.getValues('topic') },
      })
      form.setValue('topic', res.data)
    } catch (error) {
      // no-op
    } finally {
      setIsAIProcessing(false)
    }
  }

  const handleSelectNsda = (text: string) => {
    form.setValue('topic', text)
  }

  return (
    <div className="h-full flex flex-col p-4 overflow-auto noScrollbar">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, onErrors)} className="flex flex-col space-y-4">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="font-semibold">Title</FormLabel>
                <FormControl>
                  <Input
                    disabled={isLoading}
                    className=" border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                    placeholder="A clever title for your debate..."
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="topic"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex w-full items-end font-semibold">
                  Resolution
                  <span className="ml-auto flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button type="button" variant="outline" className="text-xs py-1 px-2">
                          NSDA
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="max-h-80 overflow-auto w-[420px]">
                        <DropdownMenuLabel>NSDA Resolutions (2025–2026)</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {nsdaResolutions.map((r: any, idx: number) => (
                          <DropdownMenuItem
                            key={idx}
                            onClick={() => handleSelectNsda(r.resolution)}
                            title={r.resolution}
                            className="whitespace-normal break-words"
                          >
                            <div className="flex flex-col">
                              <span className="text-xs font-semibold">{r.category || 'NSDA'}</span>
                              <span className="text-sm">{r.resolution}</span>
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleGenAiDebateProposition}
                          className="w-[35px] h-[35px] !p-0 hover:text-white ai-gradient"
                        >
                          <Brain size="15px" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Generate AI Debate Topic</TooltipContent>
                    </Tooltip>
                  </span>
                </FormLabel>
                <FormControl>
                  <Textarea
                    disabled={isLoading}
                    className=" border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                    placeholder="Resolved..."
                    {...field}
                    value={
                      isAIProcessing
                        ? "I'm thinking about a resolution for your debate..."
                        : field.value
                    }
                    maxLength={200}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex flex-col sm:flex-row w-full gap-4">
            <FormField
              control={form.control}
              name="affirmingTeamName"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel className="font-semibold">Affirming Team Name (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                      placeholder="Enter affirming team name..."
                      {...field}
                      maxLength={20}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dissentingTeamName"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel className="font-semibold">Dissenting Team Name (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                      placeholder="Enter dissenting team name..."
                      {...field}
                      maxLength={20}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="format"
            render={({ field }) => <DebateFormatFormField field={field} form={form} />}
          />

          <FormField
            control={form.control}
            name="isPublic"
            render={({ field }) => (
              <FormItem className="w-1/4 flex flex-row items-end rounded-md space-x-2 cursor-pointer">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <FormLabel className="cursor-pointer font-semibold w-full whitespace-nowrap">
                  Make Public
                </FormLabel>
              </FormItem>
            )}
          />

          {controlBtns}
        </form>
      </Form>
    </div>
  )
}
